import React, { useState } from 'react';
import { BarChart3, TrendingUp, ArrowUpRight, ArrowDownRight, Trophy, Building2, Layers, Target } from 'lucide-react';
import type { LearningProgress as LearningProgressType, TopCourse, LeaderboardEntry, DepartmentPerformance } from '@/services/PagesServices/AnalyticsService';
import type { LineChartData } from '../utils';

interface FunnelStage { label: string; value: number; color: string; }
interface ProgressSegment { label: string; value: number; pct: number; color: string; }

export interface OverviewChartsProps {
  funnelData: FunnelStage[] | null;
  progressSegments: ProgressSegment[];
  chartData: (LineChartData & { points: { x: number; y: number; hours: number; week: string }[] }) | null;
  learningProgress: LearningProgressType | null;
  topCourses: TopCourse[];
  leaderboard: LeaderboardEntry[];
  deptPerformance: DepartmentPerformance[];
}

const OverviewCharts: React.FC<OverviewChartsProps> = ({
  funnelData, progressSegments, chartData, learningProgress, topCourses,
  leaderboard, deptPerformance,
}) => {
  const [chartTooltip, setChartTooltip] = useState<{
    visible: boolean; x: number; y: number; hours: string; week: string;
  }>({ visible: false, x: 0, y: 0, hours: '', week: '' });

  return (
    <>
      {/* Enrollment Funnel + Progress Distribution */}
      <div className="analytics-grid-2">
        {funnelData && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Layers size={20} className="analytics-section-icon" />
              Enrollment Funnel
            </div>
            <div className="analytics-section-subtitle">Learner journey from enrollment to completion</div>
            <div className="analytics-funnel-container">
              {funnelData.map((stage, i) => {
                const maxVal = funnelData[0].value || 1;
                const widthPct = Math.max((stage.value / maxVal) * 100, 20);
                const conversionRate = i > 0 && funnelData[i - 1].value > 0
                  ? Math.round((stage.value / funnelData[i - 1].value) * 100) : 100;
                return (
                  <div key={i} className="analytics-funnel-stage">
                    <div className="analytics-funnel-label">
                      <span className="analytics-funnel-stage-name">{stage.label}</span>
                      <span className="analytics-funnel-stage-value">{stage.value.toLocaleString()}</span>
                    </div>
                    <div className="analytics-funnel-bar-wrapper">
                      <div className="analytics-funnel-bar" style={{ width: `${widthPct}%`, background: stage.color }} />
                    </div>
                    {i > 0 && (
                      <div className="analytics-funnel-conversion">
                        <ArrowDownRight size={12} />
                        {conversionRate}% conversion
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {progressSegments.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Target size={20} className="analytics-section-icon" />
              Progress Distribution
            </div>
            <div className="analytics-section-subtitle">How far learners are in their courses</div>
            <div className="analytics-progress-stacked-bar">
              {progressSegments.map((seg, i) => (
                seg.pct > 0 && (
                  <div key={i} className="analytics-progress-stacked-segment"
                    style={{ width: `${seg.pct}%`, background: seg.color }}
                    title={`${seg.label}: ${seg.value} (${seg.pct}%)`}
                  />
                )
              ))}
            </div>
            <div className="analytics-progress-stacked-legend">
              {progressSegments.map((seg, i) => (
                <div key={i} className="analytics-progress-stacked-legend-item">
                  <div className="analytics-progress-stacked-dot" style={{ background: seg.color }} />
                  <div className="analytics-progress-stacked-legend-info">
                    <span className="analytics-progress-stacked-label">{seg.label}</span>
                    <span className="analytics-progress-stacked-count">{seg.value?.toLocaleString()}</span>
                  </div>
                  <span className="analytics-progress-stacked-pct">{seg.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Learning Hours Chart + Learning Progress */}
      <div className="analytics-grid-2">
        {chartData && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <BarChart3 size={20} className="analytics-section-icon" />
              Learning Hours Trend
            </div>
            <div className="analytics-section-subtitle">Weekly learning hours across the platform</div>
            <div className="analytics-chart-container">
              <svg viewBox={`0 0 ${chartData.width} ${chartData.height}`} className="analytics-svg-chart" preserveAspectRatio="xMidYMid meet">
                {chartData.yLabels.map((label, i) => (
                  <g key={i}>
                    <line x1={chartData.padding} y1={label.y} x2={chartData.padding + chartData.chartW} y2={label.y}
                      stroke="var(--color-border, #e5e7eb)" strokeWidth="0.5" strokeDasharray="4,4" />
                    <text x={chartData.padding - 8} y={label.y + 4} textAnchor="end"
                      fill="var(--color-secondary-text, #6b7280)" fontSize="11">{label.value}</text>
                  </g>
                ))}
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff8c42" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#ff8c42" stopOpacity="0.02" />
                  </linearGradient>
                </defs>
                <path d={chartData.areaD} fill="url(#chartGradient)" />
                <path d={chartData.pathD} fill="none" stroke="#ff8c42" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" />
                {chartData.points.map((p, i) => (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="14" fill="transparent" className="analytics-chart-hitarea"
                      onMouseEnter={(e) => {
                        const svg = e.currentTarget.closest('svg');
                        if (!svg) return;
                        const pt = svg.createSVGPoint(); pt.x = p.x; pt.y = p.y;
                        const ctm = svg.getScreenCTM(); if (!ctm) return;
                        const screenPt = pt.matrixTransform(ctm);
                        setChartTooltip({ visible: true, x: screenPt.x, y: screenPt.y - 60,
                          hours: `${p.value} Hours`, week: p.label });
                      }}
                      onMouseLeave={() => setChartTooltip({ visible: false, x: 0, y: 0, hours: '', week: '' })}
                    />
                    <circle cx={p.x} cy={p.y} r="5" fill="#ff8c42" stroke="white" strokeWidth="2"
                      className="analytics-chart-point" style={{ pointerEvents: 'none' }} />
                    <text x={p.x} y={chartData.padding + chartData.chartH + 20} textAnchor="middle"
                      fill="var(--color-secondary-text, #6b7280)" fontSize="10">{p.label}</text>
                  </g>
                ))}
              </svg>
              {chartTooltip.visible && (
                <div className="analytics-chart-tooltip" style={{ left: chartTooltip.x, top: chartTooltip.y }}>
                  <div className="analytics-chart-tooltip-week">{chartTooltip.week}</div>
                  <div className="analytics-chart-tooltip-hours">{chartTooltip.hours}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {learningProgress && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <TrendingUp size={20} className="analytics-section-icon" />
              Learning Progress
            </div>
            <div className="analytics-section-subtitle">{learningProgress.currentMonth} summary</div>
            <div className="analytics-progress-summary">
              <div className="analytics-progress-big-number">
                <div className="analytics-progress-hours">{learningProgress.totalHours}</div>
                <div className="analytics-progress-hours-label">Total Hours</div>
              </div>
              <div className={`analytics-progress-trend-badge ${learningProgress.trend}`}>
                {learningProgress.trend === 'down' ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                {learningProgress.trendValue}%
              </div>
            </div>
            <div className="analytics-progress-bar-section">
              <div className="analytics-progress-bar-label">
                <span>Monthly Progress</span>
                <span>{Math.min(learningProgress.progressPercentage, 100)}%</span>
              </div>
              <div className="analytics-progress-bar">
                <div className="analytics-progress-fill high"
                  style={{ width: `${Math.min(learningProgress.progressPercentage, 100)}%` }} />
              </div>
            </div>
            {topCourses.length > 0 && (
              <div className="analytics-mini-section">
                <div className="analytics-mini-title">Top Courses</div>
                <div className="analytics-top-courses-list">
                  {topCourses.slice(0, 5).map((course, i) => {
                    const pct = course.total > 0 ? Math.round((course.enrolled / course.total) * 100) : 0;
                    return (
                      <div key={i} className="analytics-top-course-item">
                        <div className="analytics-top-course-rank">{i + 1}</div>
                        <div className="analytics-top-course-info">
                          <div className="analytics-top-course-name">{course.name}</div>
                          <div className="analytics-top-course-enrolled">{course.enrolled} / {course.total}</div>
                        </div>
                        <div className="analytics-top-course-bar-wrapper">
                          <div className="analytics-progress-bar" style={{ height: '6px' }}>
                            <div className="analytics-progress-fill high" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Leaderboard + Department Performance */}
      <div className="analytics-grid-2">
        {leaderboard.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Trophy size={20} className="analytics-section-icon" />
              Top Learners
            </div>
            <div className="analytics-section-subtitle">Leaderboard rankings</div>
            <div className="analytics-leaderboard-list">
              {leaderboard.slice(0, 8).map((entry) => (
                <div key={entry.rank} className="analytics-leaderboard-item">
                  <div className={`analytics-leaderboard-rank ${entry.rank === 1 ? 'gold' : entry.rank === 2 ? 'silver' : entry.rank === 3 ? 'bronze' : 'default'}`}>
                    {entry.rank}
                  </div>
                  <div className="analytics-leaderboard-initials">{entry.initials}</div>
                  <div className="analytics-leaderboard-info">
                    <div className="analytics-leaderboard-name">{entry.name}</div>
                    <div className="analytics-leaderboard-level">
                      <span className="analytics-level-badge"
                        style={{ borderColor: entry.levelColor, color: entry.levelColor }}>
                        {entry.level}
                      </span>
                      {entry.coursesCompleted} courses · {entry.certificatesEarned} certs
                    </div>
                  </div>
                  <div className="analytics-leaderboard-points-col">
                    <div className="analytics-leaderboard-points">{entry.points.toLocaleString()}</div>
                    <div className="analytics-leaderboard-points-label">pts</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {deptPerformance.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Building2 size={20} className="analytics-section-icon" />
              Department Performance
            </div>
            <div className="analytics-section-subtitle">Completion rates by department</div>
            <div className="analytics-dept-list">
              {deptPerformance.map((dept, i) => {
                const pct = dept.completionPercentage;
                return (
                  <div key={i} className="analytics-dept-item">
                    <div className="analytics-dept-header">
                      <div className="analytics-dept-name">{dept.department}</div>
                      <div className={`analytics-dept-pct ${pct >= 70 ? 'high' : pct >= 40 ? 'medium' : 'low'}`}>
                        {pct}%
                      </div>
                    </div>
                    <div className="analytics-dept-stats">
                      {dept.usersWithEnrollments}/{dept.totalUsers} users enrolled
                    </div>
                    <div className="analytics-progress-bar">
                      <div className={`analytics-progress-fill ${pct >= 70 ? 'high' : pct >= 40 ? 'medium' : 'low'}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(OverviewCharts);
